import axios from 'axios'

/** 统一 API 字段（兼容 origin_fee 蛇形命名） */
export function normalizeExamFromApi(exam) {
  if (!exam) return exam
  return {
    ...exam,
    free: Number(exam.free) || 0,
    fee: Number(exam.fee) || 0,
    originFee: Number(exam.originFee ?? exam.origin_fee ?? 0) || 0,
    officialCertified: Number(exam.officialCertified) || 0
  }
}

/** 管理员标记的官方认证测评（可用于朋友资料可见性筛选） */
export function isOfficialCertified(exam) {
  return Number(exam?.officialCertified) === 1
}

/** C 端 /exames 可见：未删除、上架、审核通过 */
export function isExamListedOnCEnd(exam) {
  if (!exam) return false
  return Number(exam.removed) !== 1 && Number(exam.state) === 1 && Number(exam.auditStatus) === 1
}

/** 数据库原始成交价（分），不做促销展示换算 */
export function examFeeCentsRaw(exam) {
  const e = normalizeExamFromApi(exam)
  return Number(e?.fee) || 0
}

/** 旧版：free=1 且 fee>0 表示限时免费（原价存在 fee 里） */
export function isLegacyLimitedTimeFree(exam) {
  const e = normalizeExamFromApi(exam)
  return Number(e.free) === 1 && examFeeCentsRaw(e) > 0
}

/** free=1 且 fee=0：永久免费 */
export function isPermanentFreeExam(exam) {
  const e = normalizeExamFromApi(exam)
  return Number(e.free) === 1 && examFeeCentsRaw(e) === 0
}

/** 限时免费：新规则或旧版兼容 */
export function isLimitedTimeFree(exam) {
  const e = normalizeExamFromApi(exam)
  if (isLegacyLimitedTimeFree(e)) return true
  return (
    Number(e.free) === 0 &&
    examFeeCentsRaw(e) === 0 &&
    examOriginFeeCents(e) > 0
  )
}

/** 0<fee<originFee 且 free=0：限时优惠 */
export function isLimitedTimePromo(exam) {
  const e = normalizeExamFromApi(exam)
  const fee = examFeeCents(e)
  const origin = examOriginFeeCents(e)
  return Number(e.free) === 0 && fee > 0 && origin > fee
}

/** 列表角标：免费 | 限时免费 | 限时优惠（限时类优先于永久免费） */
export function getExamPromoBadge(exam) {
  const e = normalizeExamFromApi(exam)
  if (!e) return null
  if (isLimitedTimeFree(e)) {
    return { type: 'limited-free', label: '限时免费' }
  }
  if (isPermanentFreeExam(e)) {
    return { type: 'free', label: '免费' }
  }
  if (isLimitedTimePromo(e)) {
    return { type: 'limited-promo', label: '限时优惠' }
  }
  return null
}

/** 测评是否可免费使用 */
export function isExamFree(exam) {
  const e = normalizeExamFromApi(exam)
  if (!e) return true
  return isPermanentFreeExam(e) || isLimitedTimeFree(e) || examFeeCentsRaw(e) === 0
}

/** 是否需要购买（已购除外） */
export function isExamChargeable(exam) {
  return !isExamFree(exam)
}

export function isExamAcquired(exam) {
  return Number(exam?.isAcquired) > 0
}

/** 展示用成交价（分）：限时免费（含旧版）为 0 */
export function examFeeCents(exam) {
  const e = normalizeExamFromApi(exam)
  if (isLimitedTimeFree(e)) return 0
  return examFeeCentsRaw(e)
}

/** 展示用原价（分） */
export function examOriginFeeCents(exam) {
  const e = normalizeExamFromApi(exam)
  if (isLegacyLimitedTimeFree(e)) return examFeeCentsRaw(e)
  return Number(e.originFee) || 0
}

export function formatExamFeeYuan(exam) {
  return (examFeeCents(exam) / 100).toFixed(2)
}

export function formatExamOriginFeeYuan(exam) {
  return (examOriginFeeCents(exam) / 100).toFixed(2)
}

/** 列表/分享用路由段：有英文简称用简称，否则用数字 id */
export function getExamRouteKey(exam) {
  const s = (exam?.examSname || '').trim()
  if (s) return s
  const id = exam?.id
  return id != null && id !== '' ? String(id) : ''
}

export function examDetailPath(exam) {
  const key = getExamRouteKey(exam)
  return key ? `/exam/${encodeURIComponent(key)}` : '/exames'
}

export function couponFromRouteQuery(query) {
  const direct = query?.coupon || query?.couponNo
  if (typeof direct === 'string' && direct.trim()) return direct.trim()
  const href = typeof query?.href === 'string' ? query.href : ''
  const i = href.indexOf('?')
  if (i < 0) return ''
  const q = new URLSearchParams(href.slice(i + 1))
  return (q.get('coupon') || q.get('couponNo') || '').trim()
}

export function recordContentOpen(kind, key) {
  const k = String(kind || '').trim()
  const id = decodeURIComponent(String(key || '').trim())
  if ((k !== 'exam' && k !== 'course') || !id) return
  const now = new Date()
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  const who = localStorage.getItem('jwt_token') ? 'u' : 'g'
  const storageKey = `content-open:${k}:${id}:${day}:${who}`
  try {
    if (sessionStorage.getItem(storageKey)) return
    sessionStorage.setItem(storageKey, '1')
  } catch {
    /* 隐私模式仍交给服务端按天去重 */
  }
  axios.post('/api/v1/content/view', { kind: k, key: id }).catch(() => {})
}

export function contentOpenFromPath(path) {
  const p = String(path || '').split('?')[0]
  const take = p.match(/^\/exam\/take\/([^/]+)/)
  if (take) return { kind: 'exam', key: take[1] }
  if (
    p.startsWith('/exam/share')
    || p.startsWith('/exam/my')
    || p.startsWith('/exam/result')
    || p.startsWith('/exam/edit')
    || p.startsWith('/exam/create')
  ) {
    return null
  }
  const exam = p.match(/^\/exam\/([^/]+)/)
  if (exam) return { kind: 'exam', key: exam[1] }
  const course = p.match(/^\/knowledge\/courses\/display\/([^/]+)/)
  if (course) return { kind: 'course', key: course[1] }
  return null
}

export function recordCouponVisit(coupon, pagePath) {
  const no = (coupon || '').trim()
  const path = String(pagePath || '').split('?')[0]
  if (!no || !path.startsWith('/')) return
  const now = new Date()
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  const who = localStorage.getItem('jwt_token') ? 'u' : 'g'
  const key = `coupon-visit:${no}:${path}:${day}:${who}`
  try {
    if (sessionStorage.getItem(key)) return
    sessionStorage.setItem(key, '1')
  } catch {
    /* 隐私模式仍交给服务端按天去重 */
  }
  axios.post('/api/v1/referral/visit', { couponNo: no, pagePath: path }).catch(() => {})
}

export function withCouponQuery(path, coupon) {
  const no = (coupon || '').trim()
  if (!no || !path) return path
  const sep = String(path).includes('?') ? '&' : '?'
  return `${path}${sep}coupon=${encodeURIComponent(no)}`
}

export function examTakePath(exam) {
  const key = getExamRouteKey(exam)
  return key ? `/exam/take/${encodeURIComponent(key)}` : '/exames'
}

export function examResultListPath(exam) {
  const key = getExamRouteKey(exam)
  return key ? `/exam/result/list/${encodeURIComponent(key)}` : '/exames'
}

/** 路由参数是否为纯数字 id */
export function isExamRouteKeyNumeric(key) {
  return /^\d+$/.test(String(key || '').trim())
}

/** 开关/布尔 → 0|1，供 exam/create、exam/update 使用 */
export function examFlag(v) {
  return v === true || v === 1 || v === '1' ? 1 : 0
}

/**
 * 组装测评提交体（仅 API 接受的字段，数值类型与后端一致）
 * @param {object} exam
 * @param {{ forUpdate?: boolean, feeInYuan?: boolean }} opts
 */
export function buildExamSubmitPayload(exam, opts = {}) {
  const { forUpdate = false, feeInYuan = true } = opts
  const feeRaw = Number(exam.fee) || 0
  const originRaw = Number(exam.originFee) || 0
  const fee = feeInYuan ? Math.round(feeRaw * 100) : feeRaw
  const originFee = feeInYuan ? Math.round(originRaw * 100) : originRaw

  const payload = {
    examName: exam.examName ?? '',
    examSname: (exam.examSname ?? '').trim(),
    profile: exam.profile ?? '',
    description: exam.description ?? '',
    thumb: exam.thumb ?? '',
    cover: exam.cover ?? '',
    questionKeys: exam.questionKeys ?? '',
    originalQuestions: exam.originalQuestions ?? '',
    questionCount: Number(exam.questionCount) || 0,
    timeLimit: Number(exam.timeLimit) || 0,
    free: examFlag(exam.free),
    fee,
    originFee,
    oneFree: examFlag(exam.oneFree),
    evaluatorUserIds: exam.evaluatorUserIds ?? '-1',
    evaluateeUserIds: exam.evaluateeUserIds ?? '-1',
    reportAverageUserIds: exam.reportAverageUserIds ?? '-1',
    reportDetailUserIds: exam.reportDetailUserIds ?? '-1',
    auditStatus: Number(exam.auditStatus) || 0,
    auditMessage: exam.auditMessage ?? '',
    auditTime: Number(exam.auditTime) || 0,
    removed: examFlag(exam.removed),
    state: Number(exam.state) || 0,
    praise: Number(exam.praise) || 0,
    feedbacks: exam.feedbacks ?? '',
    summarys: exam.summarys ?? '',
    categoryId: Number(exam.categoryId) || 0,
    stops: exam.stops ?? '',
    isHot: examFlag(exam.isHot),
    homeDisplay: examFlag(exam.homeDisplay),
    tagIds: Array.isArray(exam.tagIds)
      ? exam.tagIds.map((id) => Number(id)).filter((id) => id > 0)
      : []
  }

  if (forUpdate) {
    payload.id = Number(exam.id)
    if (exam.userId != null) payload.userId = Number(exam.userId) || 0
  }

  return payload
}

/** 列表/详情费用文案（展示成交价 fee） */
export function getExamPriceText(exam) {
  const e = normalizeExamFromApi(exam)
  if (!e) return '免费'
  if (isPermanentFreeExam(e)) {
    return '免费'
  }
  if (isLimitedTimeFree(e)) {
    return `原价 ¥${formatExamOriginFeeYuan(e)} · 限时免费`
  }
  if (isExamFree(e)) {
    return '免费'
  }
  if (isLimitedTimePromo(e)) {
    return `¥${formatExamFeeYuan(e)}（原价 ¥${formatExamOriginFeeYuan(e)}）`
  }
  if (isExamAcquired(e)) {
    return `¥${formatExamFeeYuan(e)}（${e.acquireSource === 'redeem' ? '已兑换' : '已购买'}）`
  }
  return `¥${formatExamFeeYuan(e)}`
}
